import React from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/** Catches a render error instead of letting it blank the whole app.
 *
 *  Without this, one bad value takes the entire screen to white with no text --
 *  no header, no message, nothing to tap. Someone on a job site cannot tell that
 *  apart from a dead phone or no signal, and has no way to report it usefully.
 *
 *  Realistic triggers are not exotic: a backend deploy that changes a response
 *  shape mid-session, a null where an array was expected, or a browser holding a
 *  cached bundle that no longer matches the API. Six screens were verified to go
 *  blank this way.
 *
 *  This is deliberately a class component -- componentDidCatch has no hook
 *  equivalent, so React has no other way to express an error boundary.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error, info) {
        // Nothing collects these remotely, so the console is the only record
        // available when someone reports "it went white". Keep it loud.
        console.error("Screen failed to render:", error, info?.componentStack);
    }

    render() {
        if (!this.state.failed) return this.props.children;
        return (
            <div className="min-h-screen paper-bg flex items-center justify-center p-6" data-testid="error-boundary">
                <div className="max-w-sm w-full bg-white border border-brand-charcoal/15 rounded-2xl shadow-card p-6 space-y-4">
                    <div className="flex items-center gap-2 text-brand-danger">
                        <AlertTriangle className="h-5 w-5 shrink-0" />
                        <span className="text-[10px] uppercase tracking-widest font-bold">This screen stopped working</span>
                    </div>
                    <p className="text-sm text-brand-charcoal/80">
                        Something went wrong displaying this page. Your hours are safe — nothing you
                        already saved has been lost.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full h-12 rounded-xl bg-brand-navy hover:bg-brand-navy-dark text-brand-bone font-bold flex items-center justify-center gap-2"
                        data-testid="error-boundary-reload"
                    >
                        <RotateCcw className="h-4 w-4" /> Reload the page
                    </button>
                    <p className="text-xs text-brand-charcoal/55 text-center">
                        If it keeps happening, tell your foreman which screen you were on.
                    </p>
                </div>
            </div>
        );
    }
}
